import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import { InfoIcon } from 'ui-components/components/info-icon';

import { emptyObject } from 'ui-components/default-props/empty';
import SummaryBar from '../components/summary-bar';
import CustomersByDayGraph from '../components/graphs/customers-by-day-graph';
import OrderByRegionGraph from '../components/graphs/order-by-region-graph';
import { MostValuableCustomerTable } from '../components/most-valuable-customer-table';
import InsufficientDataBannerMessage from '../components/insufficient-data-banner-message';
import { doubleDash } from '../constants';

import messages from './messages';
import style from './style.module.less';

const renderSummarySection = (summary = emptyObject) => {
  const { newCustomers, returnCustomers, totalOrderAmount, returningCustomerOrderAmount } = summary;
  const newCustomerOrderAmount = totalOrderAmount - returningCustomerOrderAmount;
  const dataIsUndefined = totalOrderAmount === undefined;
  const formattedData = [
    {
      title: <FormattedMessage {...messages.newCustomersLabel} />,
      value: dataIsUndefined ? doubleDash : <FormattedNumber value={newCustomers} />,
    },
    {
      title: <FormattedMessage {...messages.returnCustomersLabel} />,
      value: dataIsUndefined ? doubleDash : <FormattedNumber value={returnCustomers} />,
    },
    {
      title: (
        <>
          <span className={style['title-with-icon']}>
            <FormattedMessage {...messages.newCustomersPercentLabel} />
          </span>
          <FormattedMessage {...messages.gmvNew}>{(formattedMessage) => <InfoIcon tooltipText={formattedMessage} />}</FormattedMessage>
        </>
      ),
      value: dataIsUndefined ? (
        doubleDash
      ) : (
        <FormattedNumber
          maximumFractionDigits={0}
          style='percent'
          value={totalOrderAmount > 0 ? newCustomerOrderAmount / totalOrderAmount : 0}
        />
      ),
    },
    {
      title: (
        <>
          <span className={style['title-with-icon']}>
            <FormattedMessage {...messages.returnCustomersPercentLabel} />
          </span>
          <FormattedMessage {...messages.returnCustomersPercentLabel}>
            {(formattedMessage) => <InfoIcon tooltipText={formattedMessage} />}
          </FormattedMessage>
        </>
      ),
      value: dataIsUndefined ? (
        doubleDash
      ) : (
        <FormattedNumber
          maximumFractionDigits={0}
          style='percent'
          value={totalOrderAmount > 0 ? returningCustomerOrderAmount / totalOrderAmount : 0}
        />
      ),
    },
  ];
  return <SummaryBar className={style['summary-bar']} data={formattedData} />;
};

const CustomersOverview = ({ chartData = emptyObject }) => {
  const { byCountry, byInterval, summary, topSpendingCustomers } = chartData;

  const shouldShowNullStates = !summary?.totalOrderCount;

  return (
    <>
      {shouldShowNullStates && <InsufficientDataBannerMessage />}
      <div className={style['customers-by-day-graph']}>
        <CustomersByDayGraph data={byInterval} shouldShowNullStates={shouldShowNullStates} />
      </div>
      {renderSummarySection(summary)}
      <div className={style.container}>
        <div className={style['chart-section']}>
          <OrderByRegionGraph data={byCountry} />
          <p>
            <Link to='/dashboard/reports/CountryComparison'>
              <FormattedMessage {...messages.reportDataLink} />
            </Link>
          </p>
        </div>
        <div className={style['chart-section']}>
          <MostValuableCustomerTable data={topSpendingCustomers} />
          <p>
            <Link to='/dashboard/reports/Customer'>
              <FormattedMessage {...messages.customerDataLink} />
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

CustomersOverview.propTypes = {
  chartData: PropTypes.object,
};

export default CustomersOverview;
